import type { IndexDef, LevelCode, LevelStatus, ObjectiveEvent, ObjectiveState, Snapshot } from '@/api/types/readiness'
import { levelNum } from '@/api/types/readiness'
import { allObjectives } from '@/lib/indexes/registry'
import { computeIndexScore, computeScore } from '@/lib/readiness/score'
import { assertScale } from '@/lib/readiness/scale'

// ── State at date and the readiness series (Iteration 2 §2, §3, §7, Note 2) ──
// `stateAt(date)` replays the objective event log up to `date` and returns each
// objective's level and status; it powers the chart, the sparkline and the
// matrix "what changed" column. Bucketed series are the in-browser equivalent
// of `GET /readiness/series?index&period&from&to&series=score|domain`.

export interface ReplayState {
  level: LevelCode
  status: LevelStatus
  suggested_level: LevelCode | null
  score: number | null
  suggested_score: number | null
  tier: number | null
  basis: ObjectiveState['basis']
  set_by: string | null
  set_at: string | null
  note: string | null
  suggested_at: string | null
  suggested_by: string | null
}

const fresh = (): ReplayState => ({ level: 'L0', status: 'unset', suggested_level: null, score: null, suggested_score: null, tier: null, basis: 'internal', set_by: null, set_at: null, note: null, suggested_at: null, suggested_by: null })

function apply(st: ReplayState, e: ObjectiveEvent): ReplayState {
  switch (e.event_type) {
    case 'suggested': {
      const has = e.to_level != null || e.score_to != null
      if (st.status === 'unset' || st.status === 'suggested') {
        return has
          ? { ...st, status: 'suggested', suggested_level: e.to_level, suggested_score: e.score_to, suggested_at: e.occurred_at, suggested_by: e.actor }
          : { ...st, status: 'unset', suggested_level: null, suggested_score: null, suggested_at: null, suggested_by: null }
      }
      return { ...st, suggested_level: has ? e.to_level : null, suggested_score: has ? e.score_to : null, suggested_at: has ? e.occurred_at : null, suggested_by: has ? e.actor : null }
    }
    case 'confirmed':
    case 'declared':
    case 'overridden':
      return {
        ...st,
        level: e.to_level ?? st.level,
        status: e.status ?? (e.event_type === 'confirmed' ? 'confirmed' : 'declared'),
        suggested_level: null,
        suggested_score: null,
        suggested_at: null,
        suggested_by: null,
        score: e.score_to ?? (e.to_level ? null : st.score),
        tier: e.tier ?? null,
        basis: e.basis ?? st.basis,
        set_by: e.actor,
        set_at: e.occurred_at,
        note: e.note ?? null,
      }
    case 'basis_set':
      return { ...st, basis: e.basis ?? st.basis }
    default:
      return st
  }
}

// Replay the log up to and including `date` (ISO). Events are kept oldest
// first; a defensive sort keeps replay correct if a caller appends out of order.
export function stateAt(index: IndexDef, events: ObjectiveEvent[], date: string): Record<string, ReplayState> {
  const out: Record<string, ReplayState> = Object.fromEntries(allObjectives(index).map(({ objective }) => [objective.code, fresh()]))
  const sorted = [...events].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
  for (const e of sorted) {
    if (e.occurred_at > date) break
    if (!e.objective_code || !(e.objective_code in out)) continue
    out[e.objective_code] = apply(out[e.objective_code], e)
  }
  return out
}

// Effective live / reported values of a replayed state (same rules as score.ts).
export const replayLiveLevel = (s: ReplayState): LevelCode => (s.status === 'unset' ? 'L0' : s.status === 'suggested' ? (s.suggested_level ?? s.level) : s.level)
export const replayReportedLevel = (s: ReplayState): LevelCode => (s.status === 'unset' || s.status === 'suggested' ? 'L0' : s.level)
export const replayLiveScore = (s: ReplayState): number | null => (s.status === 'unset' ? null : s.status === 'suggested' ? (s.suggested_score ?? s.score) : s.score)
export const replayReportedScore = (s: ReplayState): number | null => (s.status === 'unset' || s.status === 'suggested' ? null : s.score)

export interface ScorePair {
  score: number // live, incl. suggested (index units: 0–100 for levels, 0–10 for score_0_10)
  score_confirmed_only: number
  has_suggested: boolean
}

export function scoresOf(index: IndexDef, states: Record<string, ReplayState>): ScorePair {
  const codes = allObjectives(index).map(({ objective }) => objective.code)
  const hasSuggested = codes.some((c) => states[c]?.status === 'suggested' || (states[c]?.suggested_level != null && states[c]?.suggested_level !== states[c]?.level))
  switch (assertScale(index)) {
    case 'levels':
      return {
        score: computeScore(codes.map((c) => replayLiveLevel(states[c]))),
        score_confirmed_only: computeScore(codes.map((c) => replayReportedLevel(states[c]))),
        has_suggested: hasSuggested,
      }
    case 'score_0_10':
      return {
        score: computeIndexScore(index, (c) => replayLiveScore(states[c])).overall ?? 0,
        score_confirmed_only: computeIndexScore(index, (c) => replayReportedScore(states[c])).overall ?? 0,
        has_suggested: hasSuggested,
      }
  }
}

export const scaleMax = (index: IndexDef): number => (assertScale(index) === 'levels' ? 100 : 10)

// ── Bucketing ───────────────────────────────────────────────────────────────

export type Period = 'week' | 'month' | 'quarter' | 'year'

export function bucketStart(d: Date, period: Period): Date {
  const y = d.getFullYear()
  const m = d.getMonth()
  switch (period) {
    case 'week': {
      const day = (d.getDay() + 6) % 7 // Monday = 0
      const s = new Date(y, m, d.getDate() - day)
      return s
    }
    case 'month':
      return new Date(y, m, 1)
    case 'quarter':
      return new Date(y, m - (m % 3), 1)
    case 'year':
      return new Date(y, 0, 1)
  }
}

export function addPeriod(d: Date, period: Period, n = 1): Date {
  switch (period) {
    case 'week':
      return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 7 * n)
    case 'month':
      return new Date(d.getFullYear(), d.getMonth() + n, 1)
    case 'quarter':
      return new Date(d.getFullYear(), d.getMonth() + 3 * n, 1)
    case 'year':
      return new Date(d.getFullYear() + n, 0, 1)
  }
}

export function bucketLabel(d: Date, period: Period): string {
  switch (period) {
    case 'week':
      return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    case 'month':
      return d.toLocaleDateString(undefined, { month: 'short', year: '2-digit' })
    case 'quarter':
      return `Q${Math.floor(d.getMonth() / 3) + 1} ${String(d.getFullYear()).slice(2)}`
    case 'year':
      return String(d.getFullYear())
  }
}

export interface SeriesPoint {
  bucket_start: string
  bucket_end: string // exclusive
  label: string
  score: number
  score_confirmed_only: number
  has_suggested: boolean
  evidence_count: number
  moved: Array<{ code: string; from: LevelCode; to: LevelCode; from_score: number | null; to_score: number | null }>
  by_domain: Record<string, number> // mean level (0–3) or mean score (0–10) per domain, live
  live?: boolean // the last point, cut at "now"
  future?: boolean // bucket starts after now (no data)
}

export interface AssessmentNode {
  id: string
  name: string
  created_at: string
  score: number
  baseline: boolean
}

// Plan versions (I3 §5, Note 6): the chart data endpoint gains these; drawn as
// square markers labelled with the version label, distinct from assessment nodes.
// Plan version marker (I3 §5, amended I5 §9): submitted versions only, placed
// at their submission date. `created_at` keeps the field name used by the chart
// but carries `submitted_at`.
export interface PlanVersionMarker {
  id: string
  label: string
  created_at: string
  completeness: number // 0–100
  n?: number
  submitted_by?: string
  csa_reference?: string | null
}

export interface Series {
  points: SeriesPoint[]
  nodes: AssessmentNode[]
  max: number
  started_at: string | null // first event
  plan_versions?: PlanVersionMarker[]
}

function domainMeans(index: IndexDef, states: Record<string, ReplayState>): Record<string, number> {
  const scale = assertScale(index)
  const out: Record<string, number> = {}
  index.domains.forEach((d) => {
    if (scale === 'levels') {
      const ls = d.objectives.map((o) => levelNum(replayLiveLevel(states[o.code])))
      out[d.code] = ls.length ? Math.round((10 * ls.reduce((a, b) => a + b, 0)) / ls.length) / 10 : 0
    } else {
      const ss = d.objectives.map((o) => replayLiveScore(states[o.code])).filter((x): x is number => x != null)
      out[d.code] = ss.length ? Math.round((10 * ss.reduce((a, b) => a + b, 0)) / ss.length) / 10 : 0
    }
  })
  return out
}

// Build the bucketed series. `from`/`to` bound the buckets; the last bucket is
// cut at `now` and flagged live. Each point is the state at the bucket's end
// (or now), with the moves and evidence that landed inside the bucket.
export function buildSeries(index: IndexDef, events: ObjectiveEvent[], snapshots: Snapshot[], opts: { period: Period; from: Date; to: Date; now?: Date }): Series {
  const now = opts.now ?? new Date()
  const codes = allObjectives(index).map(({ objective }) => objective.code)
  const sorted = [...events].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))
  const points: SeriesPoint[] = []
  let cursor = bucketStart(opts.from, opts.period)
  const end = bucketStart(opts.to, opts.period)
  let guard = 0
  while (cursor <= end && guard++ < 400) {
    const next = addPeriod(cursor, opts.period)
    const future = cursor > now
    const cutRaw = next > now ? now : new Date(next.getTime() - 1)
    const cut = cutRaw.toISOString()
    const before = stateAt(index, sorted, new Date(cursor.getTime() - 1).toISOString())
    const at = stateAt(index, sorted, cut)
    const { score, score_confirmed_only, has_suggested } = scoresOf(index, at)
    const inBucket = sorted.filter((e) => e.occurred_at >= cursor.toISOString() && e.occurred_at <= cut)
    const moved = codes
      .map((c) => ({ code: c, from: replayLiveLevel(before[c]), to: replayLiveLevel(at[c]), from_score: replayLiveScore(before[c]), to_score: replayLiveScore(at[c]) }))
      .filter((m) => (assertScale(index) === 'levels' ? m.from !== m.to : m.from_score !== m.to_score))
    points.push({
      bucket_start: cursor.toISOString(),
      bucket_end: next.toISOString(),
      label: bucketLabel(cursor, opts.period),
      score,
      score_confirmed_only,
      has_suggested,
      evidence_count: inBucket.filter((e) => e.event_type === 'evidence_added').length,
      moved,
      by_domain: domainMeans(index, at),
      live: !future && next > now,
      future,
    })
    cursor = next
  }
  const nodes: AssessmentNode[] = snapshots
    .filter((s) => new Date(s.created_at) >= opts.from && new Date(s.created_at) <= opts.to)
    .map((s) => ({ id: s.id, name: s.name, created_at: s.created_at, score: s.score, baseline: s.baseline }))
  return { points, nodes, max: scaleMax(index), started_at: sorted[0]?.occurred_at ?? null }
}

// Score card sparkline (§3.1): the last six assessment points, or with fewer
// than six, the event log bucketed monthly. Last point = the live score.
export function sparklineSeries(index: IndexDef, events: ObjectiveEvent[], snapshots: Snapshot[], live: { score: number; has_suggested: boolean }, now = new Date()): Array<{ score: number; date: string; has_suggested: boolean }> {
  if (snapshots.length >= 6) {
    return [...snapshots.slice(-6).map((s) => ({ score: s.score, date: s.created_at, has_suggested: false })), { score: live.score, date: now.toISOString(), has_suggested: live.has_suggested }]
  }
  if (events.length === 0) return [...snapshots.slice(-6).map((s) => ({ score: s.score, date: s.created_at, has_suggested: false })), { score: live.score, date: now.toISOString(), has_suggested: live.has_suggested }]
  const first = new Date([...events].sort((a, b) => a.occurred_at.localeCompare(b.occurred_at))[0].occurred_at)
  const from = new Date(Math.max(first.getTime(), addPeriod(bucketStart(now, 'month'), 'month', -11).getTime()))
  const series = buildSeries(index, events, snapshots, { period: 'month', from, to: now, now })
  const pts = series.points.filter((p) => !p.future).map((p) => ({ score: p.score, date: p.bucket_start, has_suggested: p.has_suggested }))
  if (pts.length) pts[pts.length - 1] = { score: live.score, date: now.toISOString(), has_suggested: live.has_suggested }
  // Fewer than two buckets (a short log) but saved assessments: draw those and
  // the live point so the score card always has its sparkline (I4 §7).
  if (pts.length < 2 && snapshots.length) return [...snapshots.slice(-6).map((s) => ({ score: s.score, date: s.created_at, has_suggested: false })), { score: live.score, date: now.toISOString(), has_suggested: live.has_suggested }]
  return pts
}
